import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { IFilters } from '@app/infrastructure/data-list/grid/api-grid-filters/filters';
import { FilterAssistant } from '@app/infrastructure/filters/filter-assistant';
import { GridFilterConfig } from '@app/infrastructure/filters/grid-filters/grid-filter.model';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type applyCbFunction = (filtersFormValue: Record<string, unknown>) => Record<string, unknown>;

@Injectable()
export class FilterSelection<T> {
	private filtersForm: FormGroup = new FormGroup({});
	private stateFilter?: IFilters<T>;

	private serializedAppliedForm?: string;
	private serializedDefaultFormValue?: string;
	private _isFilterNotChanged = true;
	private _isDefaultFiltersApplied = true;
	private cancel$ = new BehaviorSubject<void>(undefined);
	private destroy$ = new Subject<void>();

	constructor(private applyCb?: applyCbFunction) {}

	public get isFilterNotChanged(): boolean {
		return this._isFilterNotChanged;
	}

	public get isDefaultFiltersApplied(): boolean {
		return this._isDefaultFiltersApplied;
	}

	public static create<T>(applyCb?: applyCbFunction): FilterSelection<T> {
		return new FilterSelection(applyCb);
	}

	public getFormGroup(): FormGroup {
		return this.filtersForm;
	}

	public get isFiltersFormInvalid(): boolean {
		return this.filtersForm.invalid;
	}

	public getControl(controlName: string): AbstractControl {
		return this.filtersForm.controls[controlName];
	}

	public registerFiltersForm(
		filterConfig: Pick<GridFilterConfig<T>, 'prop' | 'type'>[],
		stateFilter: IFilters<T>,
	): void {
		this.stateFilter = stateFilter;
		this.filtersForm = this.getInitFiltersForm(filterConfig);
		this.serializedAppliedForm = JSON.stringify(this.filtersForm.value);

		this.serializedDefaultFormValue = JSON.stringify(
			this.castEmptyValuesToNull({
				...this.filtersForm.value,
				...(this.stateFilter ? this.stateFilter.getDefaultFilters() : {}),
			}),
		);

		combineLatest([this.stateFilter.changes(), this.cancel$])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([{ filters }]) => {
				const controlsConfig = this.getControlsConfig(filterConfig, filters);

				this.filtersForm.patchValue(controlsConfig);

				this.serializedAppliedForm = JSON.stringify(this.castEmptyValuesToNull(this.filtersForm.value));
				this._isFilterNotChanged = true;
			});

		this.filtersForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			this._isDefaultFiltersApplied =
				this.serializedDefaultFormValue === JSON.stringify(this.castEmptyValuesToNull(value));
			this._isFilterNotChanged = JSON.stringify(this.castEmptyValuesToNull(value)) === this.serializedAppliedForm;
		});
	}

	public reset(): void {
		this.stateFilter?.reset();
	}

	public cancel(): void {
		this.cancel$.next();
	}

	public apply(): void {
		this.stateFilter?.update(this.applyCb ? this.applyCb(this.filtersForm?.value) : this.filtersForm?.value);
	}

	public dispose(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private castEmptyValuesToNull(obj: Record<keyof T, unknown>): Record<keyof T, unknown> {
		return Object.entries(obj).reduce<Record<keyof T, unknown>>((accum, [key, value]: [string, unknown]) => {
			if (typeof value === 'number') {
				return { ...accum, [key]: value };
			}

			if (!value) {
				return { ...accum, [key]: null };
			}

			if (Array.isArray(value)) {
				return { ...accum, [key]: value.length ? value : null };
			}

			if (typeof value === 'object') {
				return { ...accum, [key]: Object.keys(value).length ? value : null };
			}

			return { ...accum, [key]: value ? value : null };
		}, {} as Record<keyof T, unknown>);
	}

	private getInitFiltersForm(filterConfig: Pick<GridFilterConfig<T>, 'prop' | 'type'>[]): FormGroup {
		const config = filterConfig.reduce(
			(controls, filterItem) => ({
				...controls,
				[filterItem.prop]: FilterAssistant.getFilterItemValueBasedOnFilterType(filterItem.type),
			}),
			{},
		);

		return new FormGroup(config);
	}

	private getControlsConfig(
		filterConfig: Pick<GridFilterConfig<T>, 'prop' | 'type'>[],
		filters: T,
	): Record<keyof T, string> | Record<string, never> {
		return filterConfig.reduce((controls, filterItem) => {
			const filterItemValue =
				filters[filterItem.prop] || FilterAssistant.getFilterItemValueBasedOnFilterType(filterItem.type).value || null;

			return { ...controls, [filterItem.prop]: filterItemValue };
		}, {});
	}
}
